import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMyFantasyFams, useWeeklyLeaderboard } from "../../../lib/api/fantasy-fams";
import { useMySubscription } from "../../../lib/api/subscription";
import { BankrollTabs, LeaderboardRow, formatPctSigned } from "./_shared";
import type { LeaderboardBankrollTier } from "../../../lib/types";

// /fantasy/weekly — Fantasy Fams Weekly leaderboard. Renders the selected
// bankroll pool's rank-ordered Fantasy Fams + their composite return for the
// week. The caller's own Fantasy Fams are highlighted inline.
export function WeeklyLeaderboardRoute() {
	const [bankroll, setBankroll] = useState<LeaderboardBankrollTier>("10k");
	const leaderboard = useWeeklyLeaderboard(bankroll);

	// Free users see all three tabs but can only open the $10K pool; the
	// $100K / $1M tabs are locked.
	const isPaid = useMySubscription().data?.status === "active";

	// Highlight the caller's own Fantasy Fams. Fails open — a signed-out
	// spectator's /me query just errors and we render without emphasis.
	const myFams = useMyFantasyFams();
	const myFamSlugs = useMemo(
		() => new Set((myFams.data?.fantasyFams ?? []).map((f) => f.slug)),
		[myFams.data],
	);

	return (
		<div className="mx-auto max-w-4xl space-y-6 px-4 py-8">
			<header className="space-y-1">
				<Link
					to="/fantasy"
					className="text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
				>
					← Fantasy
				</Link>
				<h1 className="text-2xl font-bold">Fantasy Fams: Weekly</h1>
				<p className="text-sm text-[var(--color-muted)]">
					Each Fantasy Fam's composite, scored Mon → Fri. Each bankroll tier
					competes in its own pool.
				</p>
			</header>

			<BankrollTabs value={bankroll} onChange={setBankroll} isPaid={isPaid} />

			{leaderboard.isLoading && (
				<p className="text-sm text-[var(--color-muted)]">Loading leaderboard…</p>
			)}
			{leaderboard.isError && (
				<p className="text-sm text-[var(--color-danger)]">
					Could not load leaderboard.{" "}
					{leaderboard.error instanceof Error ? leaderboard.error.message : ""}
				</p>
			)}

			{leaderboard.data && (
				<>
					{leaderboard.data.period && (
						<section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] p-4">
							<p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
								Week
							</p>
							<p className="text-lg font-semibold">
								{leaderboard.data.period}
							</p>
						</section>
					)}

					{leaderboard.data.entries.length === 0 ? (
						<EmptyState />
					) : (
						<section className="space-y-2">
							{leaderboard.data.entries.map((entry) => (
								<LeaderboardRow
									key={entry.famId}
									entry={entry}
									isYours={
										!!entry.famSlug && myFamSlugs.has(entry.famSlug)
									}
									showTickers
								/>
							))}
						</section>
					)}
				</>
			)}

			<footer className="border-t border-[var(--color-border)] pt-4 text-center text-xs text-[var(--color-muted)]">
				Returns are composite-weighted, simulated paper trades. See{" "}
				<a
					href="https://hedgefam.com/disclosures"
					className="underline hover:text-[var(--color-fg)]"
				>
					disclosures
				</a>
				.
			</footer>
		</div>
	);
}

function EmptyState() {
	return (
		<div className="rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-bg-elev)] p-6 text-center">
			<p className="text-sm text-[var(--color-muted)]">
				No Fams on the board yet this week. Be the first — opt your Fam into
				Fantasy Fams Weekly from settings.
			</p>
			<p className="mt-2 text-xs text-[var(--color-muted)]">
				Returns kick in once the week settles Friday 4pm ET. Current rank:{" "}
				<span className="font-mono">{formatPctSigned(0)}</span>
			</p>
		</div>
	);
}
